import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Card,
  Col,
  Row,
  Statistic,
  Select,
  Tag,
  Progress,
  Spin,
  Alert,
} from 'antd'
import {
  ShoppingOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  HomeOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FieldTimeOutlined,
  FireOutlined,
  CheckSquareOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { useState, useEffect, useCallback } from 'react'
import {
  fetchDashboardOverview,
  fetchCourseClasses,
  fetchBatches,
} from '../api/dashboard'
import type { CourseDashboard, ClassDashboard } from '../api/dashboard'

export const Route = createFileRoute('/')({
  component: DashboardPage,
})

function CountdownTimer({ targetTime }: { targetTime: string }) {
  const [diff, setDiff] = useState(getDiff(targetTime))

  useEffect(() => {
    const timer = setInterval(() => {
      setDiff(getDiff(targetTime))
    }, 1000)
    return () => clearInterval(timer)
  }, [targetTime])

  if (diff.total <= 0) {
    return <span style={{ color: '#52c41a' }}>已开始</span>
  }

  return (
    <span style={{ color: '#1890ff', fontWeight: 600, fontSize: 13 }}>
      {diff.days > 0 && `${diff.days}天 `}
      {String(diff.hours).padStart(2, '0')}:
      {String(diff.minutes).padStart(2, '0')}:
      {String(diff.seconds).padStart(2, '0')}
    </span>
  )
}

function getDiff(targetTime: string) {
  const target = new Date(targetTime).getTime()
  const now = Date.now()
  const total = target - now
  const seconds = Math.floor((total / 1000) % 60)
  const minutes = Math.floor((total / 1000 / 60) % 60)
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24)
  const days = Math.floor(total / (1000 * 60 * 60 * 24))
  return { total, days, hours, minutes, seconds }
}

function DashboardPage() {
  const [selectedBatchId, setSelectedBatchId] = useState<number | undefined>(undefined)

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'overview', selectedBatchId],
    queryFn: () => fetchDashboardOverview(selectedBatchId),
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  })

  const { data: batches } = useQuery({
    queryKey: ['batches'],
    queryFn: fetchBatches,
  })

  if (isLoading && !data) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        type="error"
        message="加载仪表盘数据失败"
        description={error.message}
        showIcon
      />
    )
  }

  if (!data) return null

  return (
    <div className="p-4">
      {/* 批次选择器 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
          仪表盘
        </h2>
        <Select
          value={selectedBatchId}
          onChange={setSelectedBatchId}
          placeholder="选择批次"
          style={{ width: 240 }}
          allowClear
          options={batches?.map(b => ({ label: b.name, value: b.id }))}
        />
      </div>

      {/* 汇总统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic title="总订单数" value={data.totalOrders} prefix={<ShoppingOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic title="今日新增" value={data.todayOrders} prefix={<ThunderboltOutlined />} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic title="待支付" value={data.pendingPaymentOrders} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="待签约"
              value={data.pendingContractOrders}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic title="待安排住宿" value={data.pendingAccommodationOrders} prefix={<HomeOutlined />} valueStyle={{ color: '#13c2c2' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic title="待分班" value={data.pendingAllocationOrders} prefix={<TeamOutlined />} valueStyle={{ color: '#eb2f96' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic title="已分班(完成)" value={data.completedOrders} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic title="已取消" value={data.cancelledOrders} prefix={<CloseCircleOutlined />} valueStyle={{ color: '#8c8c8c' }} />
          </Card>
        </Col>
      </Row>

      {/* 报名转化漏斗 */}
      <ConversionFunnel data={data} />

      {/* 课程销售时间线 */}
      <CourseTimeline courses={data.courses || []} />
    </div>
  )
}

function ConversionFunnel({ data }: { data: { totalOrders: number; pendingPaymentOrders: number; pendingContractOrders: number; pendingAccommodationOrders: number; pendingAllocationOrders: number; completedOrders: number; cancelledOrders: number; refundedOrders: number; contractEnabled: boolean } }) {
  const activeTotal = data.totalOrders - data.cancelledOrders - data.refundedOrders

  const steps = data.contractEnabled
    ? [
        { label: '已下单', count: data.totalOrders, color: '#1890ff' },
        { label: '已支付', count: activeTotal - data.pendingPaymentOrders, color: '#2f54eb' },
        { label: '已签约', count: activeTotal - data.pendingPaymentOrders - data.pendingContractOrders, color: '#722ed1' },
        { label: '已选住宿', count: data.pendingAllocationOrders + data.completedOrders, color: '#13c2c2' },
        { label: '已分班', count: data.completedOrders, color: '#52c41a' },
      ]
    : [
        { label: '已下单', count: data.totalOrders, color: '#1890ff' },
        { label: '已支付', count: activeTotal - data.pendingPaymentOrders, color: '#2f54eb' },
        { label: '已选住宿', count: data.pendingAllocationOrders + data.completedOrders, color: '#13c2c2' },
        { label: '已分班', count: data.completedOrders, color: '#52c41a' },
      ]

  return (
    <Card
      title="报名转化漏斗"
      size="small"
      style={{ marginBottom: 24 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 0', gap: 0 }}>
        {steps.map((step, i) => {
          const prevCount = i > 0 ? steps[i - 1].count : step.count
          const rate = prevCount > 0 ? Math.round((step.count / prevCount) * 100) : 0
          return (
            <div key={step.label} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                textAlign: 'center',
                minWidth: 100,
                padding: '12px 8px',
                background: `${step.color}15`,
                borderRadius: 8,
                border: `1px solid ${step.color}30`,
              }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: step.color }}>{step.count}</div>
                <div style={{ fontSize: 12, color: '#595959', marginTop: 2 }}>{step.label}</div>
                {i > 0 && (
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                    转化率 {rate}%
                  </div>
                )}
              </div>
              {i < steps.length - 1 && (
                <div style={{ padding: '0 4px', color: '#bfbfbf', fontSize: 16 }}>→</div>
              )}
            </div>
          )
        })}
      </div>
      <div style={{ textAlign: 'center', color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>
        已取消: {data.cancelledOrders} | 已退款: {data.refundedOrders}
      </div>
    </Card>
  )
}

function CourseTimeline({ courses }: { courses: CourseDashboard[] }) {
  const upcoming = courses.filter(c => c.saleStatus === 'upcoming')
  const selling = courses.filter(c => c.saleStatus === 'selling')
  const ended = courses.filter(c => c.saleStatus === 'ended')

  return (
    <Card title="课程销售时间线" size="small">
      {courses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#8c8c8c' }}>当前批次暂无课程</div>
      ) : (
        <div>
          {upcoming.length > 0 && (
            <CourseGroup
              title="即将开售"
              icon={<FieldTimeOutlined />}
              color="#1890ff"
              courses={upcoming}
              showCountdown
            />
          )}
          {selling.length > 0 && (
            <CourseGroup
              title="正在销售"
              icon={<FireOutlined />}
              color="#52c41a"
              courses={selling}
            />
          )}
          {ended.length > 0 && (
            <CourseGroup
              title="已结束"
              icon={<CheckSquareOutlined />}
              color="#8c8c8c"
              courses={ended}
            />
          )}
        </div>
      )}
    </Card>
  )
}

function CourseGroup({
  title,
  icon,
  color,
  courses,
  showCountdown = false,
}: {
  title: string
  icon: React.ReactNode
  color: string
  courses: CourseDashboard[]
  showCountdown?: boolean
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <Tag color={color} style={{ margin: 0, fontSize: 13, padding: '2px 8px' }}>
          {icon} {title}
        </Tag>
        <span style={{ marginLeft: 8, color: '#8c8c8c', fontSize: 12 }}>{courses.length} 门课程</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {courses.map(course => (
          <CourseCard key={course.courseId} course={course} showCountdown={showCountdown} />
        ))}
      </div>
    </div>
  )
}

function CourseCard({ course, showCountdown }: { course: CourseDashboard; showCountdown?: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const [classes, setClasses] = useState<ClassDashboard[]>([])
  const [loadingClasses, setLoadingClasses] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const handleExpand = useCallback(async () => {
    if (!expanded && classes.length === 0 && !loadError) {
      setLoadingClasses(true)
      setLoadError(false)
      try {
        const data = await fetchCourseClasses(course.courseId)
        setClasses(data)
      } catch {
        setLoadError(true)
      }
      setLoadingClasses(false)
    }
    if (loadError) {
      // Reset error so retry is possible
      setLoadError(false)
      setClasses([])
    }
    setExpanded(!expanded)
  }, [expanded, classes.length, course.courseId, loadError])

  const percent = course.totalCapacity > 0
    ? Math.min(Math.round((course.enrolled / course.totalCapacity) * 100), 100)
    : 0

  let strokeColor = '#52c41a'
  if (course.remaining <= 0) strokeColor = '#ff4d4f'
  else if (course.remaining <= 5) strokeColor = '#ff4d4f'
  else if (course.remaining <= 10) strokeColor = '#faad14'

  const timeStr = `${formatTime(course.enrollStartTime)} ~ ${formatTime(course.enrollEndTime)}`

  return (
    <Card
      size="small"
      style={{ background: '#fafafa', cursor: 'pointer' }}
      onClick={handleExpand}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{course.courseName}</span>
            {course.remaining <= 0 && course.totalCapacity > 0 && (
              <Tag color="red" style={{ fontSize: 11 }}>已售罄</Tag>
            )}
          </div>
          <div style={{ color: '#8c8c8c', fontSize: 12 }}>{timeStr}</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {showCountdown && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>距离开售</div>
              <CountdownTimer targetTime={course.enrollStartTime} />
            </div>
          )}

          <div style={{ textAlign: 'center', minWidth: 50 }}>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>剩余/总容量</div>
            <div>
              <span style={{ fontWeight: 600, color: course.remaining <= 5 ? '#ff4d4f' : '#000' }}>{course.remaining}</span>
              <span style={{ color: '#8c8c8c' }}>/{course.totalCapacity}</span>
            </div>
          </div>

          <div style={{ minWidth: 120 }}>
            <Progress
              percent={percent}
              strokeColor={strokeColor}
              size="small"
              format={(p) => `${p}%`}
            />
          </div>

          {expanded ? <DownOutlined style={{ color: '#bfbfbf' }} /> : <RightOutlined style={{ color: '#bfbfbf' }} />}
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }} onClick={e => e.stopPropagation()}>
          {loadingClasses ? (
            <div style={{ textAlign: 'center', padding: 12 }}><Spin size="small" /></div>
          ) : loadError ? (
            <div style={{ textAlign: 'center', padding: 12, color: '#ff4d4f' }}>
              加载失败，请收起后重新展开重试
            </div>
          ) : classes.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#8c8c8c', padding: 12 }}>暂无班级数据</div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
                {classes.map(cls => {
                  const clsPercent = cls.totalCapacity > 0
                    ? Math.min(Math.round((cls.enrolled / cls.totalCapacity) * 100), 100)
                    : 0
                  let clsColor = '#52c41a'
                  if (cls.remaining <= 0) clsColor = '#ff4d4f'
                  else if (cls.remaining <= 5) clsColor = '#ff4d4f'
                  else if (cls.remaining <= 10) clsColor = '#faad14'

                  return (
                    <div key={cls.classId} style={{
                      padding: '8px 12px',
                      background: '#fff',
                      borderRadius: 6,
                      border: '1px solid #f0f0f0',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontWeight: 500, fontSize: 13 }}>{cls.className}</span>
                        <Tag style={{ fontSize: 11 }}>{cls.regionName}</Tag>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: '#595959' }}>
                          {cls.enrolled}/{cls.totalCapacity}
                        </span>
                        <Progress
                          percent={clsPercent}
                          strokeColor={clsColor}
                          size="small"
                          style={{ flex: 1 }}
                          format={() => ''}
                        />
                        <span style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: cls.remaining <= 5 ? '#ff4d4f' : '#52c41a',
                        }}>
                          剩 {cls.remaining}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

function formatTime(ts: string): string {
  if (!ts) return '-'
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}
